import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { ShoppingCart } from 'lucide-react';
import { Card, CardTitle, CardDescription } from './Card';
import { EmptyState } from './EmptyState';
import { StatusPill } from './StatusPill';
import { DataTable } from './DataTable';
import { api } from '@/lib/api';
import { money, date } from '@/lib/format';

interface ProjectPO {
  id: string;
  name: string;
  supplier_id: string;
  supplier_name?: string;
  transaction_date: string;
  status: string;
  grand_total: string;
}

export function ProjectPurchaseOrdersPanel({ projectID }: { projectID: string }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['project-purchase-orders', projectID],
    queryFn:  () => api<{ items: ProjectPO[] }>(`/projects/projects/${projectID}/purchase-orders`),
    enabled:  !!projectID,
  });
  const items = data?.items ?? [];

  const columns = useMemo<ColumnDef<ProjectPO>[]>(() => [
    {
      accessorKey: 'name', header: 'PO',
      cell: (info) => (
        <Link
          to={`/accounting/purchase-orders/${info.row.original.id}` as never}
          onClick={(e) => e.stopPropagation()}
          className="font-mono text-caption font-medium text-text-primary hover:text-accent hover:underline underline-offset-2"
        >
          {info.getValue<string>()}
        </Link>
      ),
    },
    {
      accessorKey: 'supplier_name', header: 'Supplier',
      cell: (info) => {
        const row = info.row.original;
        const label = row.supplier_name || row.supplier_id;
        return row.supplier_id ? (
          <Link
            to={`/accounting/suppliers/${row.supplier_id}` as never}
            onClick={(e) => e.stopPropagation()}
            className="text-text-primary hover:text-accent hover:underline underline-offset-2"
          >
            {label}
          </Link>
        ) : <span className="text-text-primary">{label}</span>;
      },
    },
    { accessorKey: 'transaction_date', header: 'Date', cell: (info) => <span className="text-text-secondary">{date(info.getValue<string>())}</span> },
    {
      accessorKey: 'status', header: 'Status',
      cell: (info) => {
        const v = info.getValue<string>();
        return <StatusPill tone={v === 'Completed' ? 'success' : 'accent'} withDot={false}>{v}</StatusPill>;
      },
    },
    { accessorKey: 'grand_total', header: 'Total', meta: { align: 'right' }, cell: (info) => <span className="num">{money(info.getValue<string>())}</span> },
  ], []);

  if (!isLoading && items.length === 0) return null;

  return (
    <Card padded={false}>
      <div className="px-5 py-3 border-b border-hairline">
        <CardTitle>
          <ShoppingCart className="size-4 inline mr-1.5 text-accent" /> Purchase Orders
        </CardTitle>
        <CardDescription>Purchase Orders charged against this project.</CardDescription>
      </div>
      <DataTable
        columns={columns}
        data={items}
        loading={isLoading}
        searchPlaceholder="Search purchase orders…"
        onRowClick={(row) => navigate({ to: `/accounting/purchase-orders/${row.id}` as never })}
        emptyState={<EmptyState icon={ShoppingCart} title="No purchase orders" description="No POs are charged against this project yet." />}
      />
    </Card>
  );
}
